import { useEffect, useState } from 'react';

interface ApiStatus {
  market: 'open' | 'closed' | 'extended-hours';
  earlyHours: boolean;
  afterHours: boolean;
  nyse: string;
  serverTime: string;
  stale?: boolean;
}

type SessionState = 'OPEN' | 'PRE-MARKET' | 'AFTER-HOURS' | 'CLOSED';

/** Current time-of-day in America/New_York, independent of the viewer's zone */
function etNow(d: Date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hour = parseInt(get('hour'), 10) % 24; // '24' at midnight in some engines
  const minute = parseInt(get('minute'), 10);
  return {
    day: days.indexOf(get('weekday')),
    mins: hour * 60 + minute,
    clock: `${String(hour).padStart(2, '0')}:${get('minute')}:${get('second')}`,
  };
}

// Regular NYSE sessions in minutes-since-midnight ET
const PRE_START = 4 * 60; // 04:00
const OPEN_START = 9 * 60 + 30; // 09:30
const CLOSE = 16 * 60; // 16:00
const AFTER_END = 20 * 60; // 20:00

function scheduleState(day: number, mins: number): SessionState {
  if (day === 0 || day === 6) return 'CLOSED';
  if (mins >= OPEN_START && mins < CLOSE) return 'OPEN';
  if (mins >= PRE_START && mins < OPEN_START) return 'PRE-MARKET';
  if (mins >= CLOSE && mins < AFTER_END) return 'AFTER-HOURS';
  return 'CLOSED';
}

/** Minutes until the next regular 09:30 ET open (weekends skipped) */
function minutesToNextOpen(day: number, mins: number): number {
  if (day >= 1 && day <= 5 && mins < OPEN_START) return OPEN_START - mins;
  let total = 1440 - mins;
  let d = (day + 1) % 7;
  while (d === 0 || d === 6) {
    total += 1440;
    d = (d + 1) % 7;
  }
  return total + OPEN_START;
}

function fmtDuration(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function MarketHours() {
  const [api, setApi] = useState<ApiStatus | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Authoritative status (holiday/early-close aware) — poll every 60s
  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch('/api/market-status');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setApi(await response.json());
      } catch {
        setApi(null); // fall back to the client-side schedule
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  // 1s ET clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const { day, mins, clock } = etNow(now);
  const sched = scheduleState(day, mins);

  let state: SessionState = sched;
  if (api) {
    if (api.market === 'open') state = 'OPEN';
    else if (api.earlyHours) state = 'PRE-MARKET';
    else if (api.afterHours) state = 'AFTER-HOURS';
    else state = 'CLOSED';
  }

  // API closed while the regular schedule says open → holiday / early close
  const holiday = api !== null && state === 'CLOSED' && sched === 'OPEN';

  let detail = '';
  if (holiday) {
    detail = 'holiday';
  } else if (state === 'OPEN') {
    detail = `closes in ${fmtDuration(CLOSE - mins)}`;
  } else if (state === 'PRE-MARKET') {
    detail = `opens in ${fmtDuration(OPEN_START - mins)}`;
  } else {
    detail = `opens in ${fmtDuration(minutesToNextOpen(day, mins))}`;
  }

  return (
    <div className="market-hours" title="NYSE / Nasdaq regular session: 09:30–16:00 ET">
      <span className={`mh-dot mh-${state === 'OPEN' ? 'open' : state === 'CLOSED' ? 'closed' : 'ext'}`}></span>
      <span className="mh-state">{state}</span>
      <span className="mh-clock">{clock} ET</span>
      <span className="mh-detail">{detail}</span>
    </div>
  );
}
